import { Form } from "antd";
import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";

import ComboBox from "@/components/Elements/ComboBox/ComboBox";
import { useIsFormEnginePreviewMode } from "@/components/FormEngine/hooks/useIsFormEnginePreviewMode";
import { useReferentialMaterials } from "@/entities/referential/referential-materials";
import { convertItemWithSlugToOption } from "@/modules/select";

import { MATERIAL_FIELD_DEPENDENCIES } from "./constants";
import { SeverityGridPreview } from "./gridUtils";
import { useMaterialsFromSeverityGrid } from "./hooks/useMaterialsFromSeverityGrid";

const { useFormInstance } = Form;

const MaterialsComboBox = ({
  onChange = () => {},
  disabled = false,
  ...rest
}) => {
  const form = useFormInstance();
  const isPreviewMode = useIsFormEnginePreviewMode();
  const { isLoading: isLoadingReferential, materials } =
    useReferentialMaterials();
  const {
    isLoadingGridMaterials,
    isGridDefinedForAsset,
    gridMaterials,
    hasCustomComponent,
  } = useMaterialsFromSeverityGrid();
  const { t } = useTranslation();
  const isLoading = isLoadingReferential || isLoadingGridMaterials;

  const getMaterialItems = () => {
    if (isPreviewMode) {
      return SeverityGridPreview.buildSampleMaterials().map(
        convertItemWithSlugToOption
      );
    }

    if (isGridDefinedForAsset && !hasCustomComponent) {
      return gridMaterials.map(convertItemWithSlugToOption);
    }

    return materials.map(convertItemWithSlugToOption);
  };

  const initialOptions = isLoading ? [] : getMaterialItems();
  const isEmpty = initialOptions.length === 0;

  const onMaterialUpdate = (value) => {
    // we reset dependent fields before calling onChange, because this will trigger FormEngine
    // conditional visibility fields mechanism at time the Form.onValuesChange will be executed
    form.resetFields(MATERIAL_FIELD_DEPENDENCIES);
    onChange(value);
  };

  return (
    <ComboBox
      loading={isLoading}
      initialOptions={initialOptions}
      onChange={onMaterialUpdate}
      placeholder={t("form:staticField.material.placeholder")}
      {...rest}
      disabled={isEmpty || disabled}
    />
  );
};

export default MaterialsComboBox;

MaterialsComboBox.propTypes = {
  onChange: PropTypes.func,
  disabled: PropTypes.bool,
};
